import { useContext, useEffect, useRef, useState } from "react";
import { useDispatch } from "react-redux";
import { Link, useLocation } from "react-router-dom";

import { PanelCartComponent } from "@/components/ecomm/cart";
import ShoppingCartNavIcon from "@/components/ecomm/ShoppingCartNavIcon";
import VHTUpsellModal from "@/components/results_v2/urineTest/VHTUpsellModal";
import { ConsultsContext } from "@/contexts/consultsContext";
import { useLatestReadyTestContext } from "@/contexts/latestReadyTestContext";
import { useConsults } from "@/hooks/care/useConsults";
import { usePanelCart } from "@/hooks/cart/usePanelCart";
import { useIdentity } from "@/hooks/useIdentity";
import { useLoggedInUser } from "@/hooks/useLoggedInUser";
import { authService } from "@/services/auth";
import { eventNames } from "@/utils/analytics/customEventTracking";
import { analyticsClickHandler } from "@/utils/analytics/helpers";
import { cn } from "@/utils/cn";

interface HeaderProps {
  noBottomPadding?: boolean;
  noRoundedBottom?: boolean;
  bottomBanner?: React.ReactNode;
  isStickyHeader?: boolean;
}

interface MobileNavTab {
  label: string;
  to: string;
  enabled: boolean;
  showNotificationDot: boolean;
}

const Header = ({
  noBottomPadding,
  noRoundedBottom,
  bottomBanner,
  isStickyHeader = false,
}: HeaderProps) => {
  const { consultComputations } = useContext(ConsultsContext);
  const { openConsults, completedConsults } = consultComputations;

  const [menuOpen, setMenuOpen] = useState(false);
  const [isImpersonation, setIsImpersonation] = useState(false);
  const [showPlanModal, setShowPlanModal] = useState(false);
  const { isProvider } = useIdentity();
  const { consults } = useConsults();
  const { vaginalTest: latestVaginalTest, currentVaginalTest } =
    useLatestReadyTestContext();

  const location = useLocation();
  const [isBottomBannerVisible, setIsBottomBannerVisible] = useState(true);

  const drawerRef = useRef<HTMLElement>(null);
  const burgerRef = useRef<HTMLButtonElement>(null);
  const drawerWasOpen = useRef(false);

  // Escape closes the drawer; Tab cycles inside it so focus cannot reach the page behind
  useEffect(() => {
    if (!menuOpen) return;

    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === "Escape") {
        setMenuOpen(false);
        return;
      }
      if (e.key !== "Tab" || !drawerRef.current) return;

      const focusable = Array.from(
        drawerRef.current.querySelectorAll<HTMLElement>(
          "a[href], button:not([disabled])"
        )
      ).filter((el) => el.tabIndex !== -1);
      if (!focusable.length) return;

      const first = focusable[0];
      const last = focusable[focusable.length - 1];
      if (e.shiftKey && document.activeElement === first) {
        e.preventDefault();
        last.focus();
      } else if (!e.shiftKey && document.activeElement === last) {
        e.preventDefault();
        first.focus();
      }
    };

    document.addEventListener("keydown", handleKeyDown);
    return () => document.removeEventListener("keydown", handleKeyDown);
  }, [menuOpen]);

  // move focus into the drawer on open, and back to the hamburger on close
  useEffect(() => {
    if (menuOpen) {
      drawerWasOpen.current = true;
      drawerRef.current?.querySelector<HTMLElement>("button")?.focus();
    } else if (drawerWasOpen.current) {
      drawerWasOpen.current = false;
      burgerRef.current?.focus();
    }
  }, [menuOpen]);

  useEffect(() => {
    const checkScroll = () => {
      // Hide banner if we've scrolled past 1/4th of the window's height
      if (window.scrollY > window.innerHeight / 4) {
        setIsBottomBannerVisible(false);
      } else {
        setIsBottomBannerVisible(true);
      }
    };
    checkScroll();
    window.addEventListener("scroll", checkScroll);
    return () => {
      window.removeEventListener("scroll", checkScroll);
    };
  }, []);

  const currentUser = useLoggedInUser();
  const firstLetter =
    currentUser &&
    (currentUser.identity.firstName[0] || currentUser.identity.email[0]);

  const isMalePartner =
    currentUser?.identity?.registrationRoute === "male_partner";

  const hasCompletedSTIConsult =
    Array.isArray(consults) &&
    consults.some(
      (consult) => consult.type === "sti" && consult.status === "CP"
    );

  const hasOpenOrCompletedConsult =
    (Array.isArray(completedConsults) && completedConsults.length > 0) ||
    (Array.isArray(openConsults) && openConsults.length > 0);

  const resultsReady =
    currentUser && currentUser.results && currentUser.results.hasReadyTest;

  // Determine VHT status for modal
  const hasVHTInProgress = (() => {
    // Check if user has a vaginal test that's in progress (not ready and not deactivated)
    const vhtTest = latestVaginalTest || currentVaginalTest;
    if (!vhtTest) return false;
    // Test is in progress if status is not "RD" (Results Ready) and not "DA" (Deactivated)
    return vhtTest.status !== "RD" && vhtTest.status !== "DA";
  })();

  const myTreatmentsNavEnabled = hasOpenOrCompletedConsult;

  const enableShoppingCart = !isMalePartner;

  const accountPath = isProvider ? "/provider/account" : "/account/";

  // routes are splats, so the path can arrive with or without a trailing slash
  const isCurrentPath = (to: string): boolean => {
    const base = to.replace(/\/$/, "");
    return (
      location.pathname === base || location.pathname.startsWith(`${base}/`)
    );
  };

  // mirrors the desktop nav's visibility and enabled checks, in mobile drawer order
  const mobileNavTabs: MobileNavTab[] = [
    ...(!isProvider && !isMalePartner
      ? [
          {
            label: "My Tests",
            to: "/tests/",
            enabled: true,
            showNotificationDot: false,
          },
          {
            label: "My Plan",
            to: "/plan/",
            enabled: !!resultsReady,
            showNotificationDot: false,
          },
          {
            label: "My Treatments",
            to: "/treatments/",
            enabled: !!myTreatmentsNavEnabled,
            showNotificationDot: !!currentUser?.notifications?.care,
          },
        ]
      : []),
    ...(!isProvider && currentUser
      ? [
          {
            label: "Shop Care",
            to: "/care/",
            enabled: true,
            showNotificationDot: false,
          },
        ]
      : []),
  ];

  useEffect(() => {
    const queryParams = new URLSearchParams(window.location.search);
    const impersonate = queryParams.get("__impersonate");
    setIsImpersonation(!!impersonate);
  }, []);

  const {
    cart,
    isLoading,
    addToCart,
    removeFromCart,
    showCart,
    setShowCart,
    baseCartAnalyticsArgs,
    vaginalTest,
  } = usePanelCart();

  return (
    <>
      <div
        className={cn(`z-20`, {
          "lg:px-6 px-3 lg:pt-6 pt-3": noBottomPadding,
          "lg:p-6 p-3": !noBottomPadding,
          "fixed top-0 left-0 w-full": isStickyHeader,
          relative: !isStickyHeader,
        })}
      >
        {isImpersonation && (
          <p className="text-center text-red-600">{`YOU ARE IMPERSONATING ${isImpersonation}`}</p>
        )}
        <div
          className={cn(
            `relative z-10 bg-evvy-black text-evvy-white p-5 max-w-[1260px] mx-auto grid grid-cols-3 md:grid-cols-4 gap-4`,
            {
              "rounded-t-xl": noRoundedBottom,
              "rounded-xl": !noRoundedBottom || !isBottomBannerVisible,
            }
          )}
        >
          <div className="flex items-center justify-center col-start-2 row-start-1 md:justify-start md:col-start-1 md:ml-4 md:mr-auto">
            <Link
              to="/tests"
              onClick={analyticsClickHandler({
                eventName: eventNames.CLICKED_TOP_NAV,
                eventArgs: { navItem: "Home" },
              })}
            >
              <img
                className="h-8 sm:h-10"
                src="/images/evvy-logo-text-white.svg"
                loading="lazy"
                alt="Evvy Text Logo"
              />
            </Link>
          </div>

          <div className="col-span-2 mx-auto space-x-12 lg:space-x-20 items-center hidden md:flex md:col-start-2">
            {isProvider ? (
              <>
                <Link
                  to="/provider/order"
                  className={`uppercase py-2 text-sm font-semibold tracking-wider border-b-2 hover:border-evvy-blue ${
                    location.pathname.startsWith("/provider/order")
                      ? "border-evvy-blue"
                      : "border-transparent"
                  }`}
                  onClick={analyticsClickHandler({
                    eventName: eventNames.CLICKED_TOP_NAV,
                    eventArgs: { navItem: "Provider Order Test" },
                  })}
                >
                  Order A New Test
                </Link>
                <Link
                  to="/provider/tests"
                  className={`uppercase py-2 text-sm font-semibold tracking-wider border-b-2 hover:border-evvy-blue ${
                    location.pathname.startsWith("/provider/tests")
                      ? "border-evvy-blue"
                      : "border-transparent"
                  }`}
                  onClick={analyticsClickHandler({
                    eventName: eventNames.CLICKED_TOP_NAV,
                    eventArgs: { navItem: "Provider Tests" },
                  })}
                >
                  Tests
                </Link>
                <Link
                  to="/provider/resources"
                  className={`uppercase py-2 text-sm font-semibold tracking-wider border-b-2 hover:border-evvy-blue ${
                    location.pathname.startsWith("/provider/resources")
                      ? "border-evvy-blue"
                      : "border-transparent"
                  }`}
                  onClick={analyticsClickHandler({
                    eventName: eventNames.CLICKED_TOP_NAV,
                    eventArgs: { navItem: "Provider Resources" },
                  })}
                >
                  Resources
                </Link>
              </>
            ) : isMalePartner ? (
              // Male partner navigation - show My Care link
              <>
                {currentUser && (
                  <div
                    className={cn("py-2 border-b-2 hover:border-evvy-blue", {
                      "border-evvy-blue":
                        location.pathname.startsWith("/treatments/"),
                      "border-transparent":
                        !location.pathname.startsWith("/treatments/"),
                    })}
                  >
                    <Link
                      to="/treatments/"
                      className="uppercase text-sm font-semibold tracking-wider"
                      onClick={analyticsClickHandler({
                        eventName: eventNames.CLICKED_TOP_NAV,
                        eventArgs: {
                          navItem: "My Treatments",
                        },
                      })}
                    >
                      My Treatments
                    </Link>
                  </div>
                )}
              </>
            ) : (
              <>
                <Link
                  to="/tests/"
                  className={`uppercase py-2 text-sm font-semibold tracking-wider border-b-2 hover:border-evvy-blue text-nowrap ${
                    location.pathname.startsWith("/tests/")
                      ? "border-evvy-blue"
                      : "border-transparent"
                  }`}
                  onClick={analyticsClickHandler({
                    eventName: eventNames.CLICKED_TOP_NAV,
                    eventArgs: {
                      navItem: "My Tests",
                    },
                  })}
                >
                  My Tests
                </Link>

                <Link
                  to="/plan/"
                  className={cn(
                    `uppercase py-2 text-sm font-semibold tracking-wider border-b-2 hover:border-evvy-blue text-nowrap ${
                      location.pathname.startsWith("/plan/")
                        ? "border-evvy-blue"
                        : "border-transparent"
                    }`,
                    {
                      "opacity-50": !resultsReady,
                    }
                  )}
                  onClick={(e) => {
                    analyticsClickHandler({
                      eventName: eventNames.CLICKED_TOP_NAV,
                      eventArgs: { navItem: "My Plan" },
                    })();

                    // If results aren't ready, prevent navigation and open modal instead
                    if (!resultsReady) {
                      e.preventDefault();
                      setShowPlanModal(true);
                    }
                  }}
                >
                  My Plan
                </Link>

                <span className="flex items-center">
                  <Link
                    to="/treatments/"
                    className={cn(
                      `uppercase py-2 text-sm font-semibold tracking-wider border-b-2 hover:border-evvy-blue text-nowrap`,
                      {
                        "pointer-events-none opacity-50":
                          !myTreatmentsNavEnabled,
                        "border-evvy-blue":
                          location.pathname.startsWith("/treatments/"),
                        "border-transparent":
                          !location.pathname.startsWith("/treatments/"),
                      }
                    )}
                    onClick={analyticsClickHandler({
                      eventName: eventNames.CLICKED_TOP_NAV,
                      eventArgs: { navItem: "My Treatments" },
                    })}
                  >
                    My Treatments
                  </Link>
                  {currentUser?.notifications?.care && (
                    <span className="inline-block w-2 h-2 ml-1 align-top bg-dv-orange rounded-full"></span>
                  )}
                </span>

                {currentUser && (
                  <div
                    className={cn("py-2 border-b-2 hover:border-evvy-blue", {
                      "border-evvy-blue":
                        location.pathname.startsWith("/care/"),
                      "border-transparent":
                        !location.pathname.startsWith("/care/"),
                    })}
                  >
                    <Link
                      to={
                        hasCompletedSTIConsult &&
                        !resultsReady &&
                        consults?.length === 1
                          ? // just take them directly to their past completed STI consult if that is the only consult they have
                            "/care/past-treatments"
                          : "/care/"
                      }
                      className="uppercase text-sm font-semibold tracking-wider text-nowrap"
                      onClick={analyticsClickHandler({
                        eventName: eventNames.CLICKED_TOP_NAV,
                        eventArgs: {
                          navItem: "Shop Care",
                        },
                      })}
                    >
                      Shop Care
                    </Link>
                  </div>
                )}
              </>
            )}
          </div>

          <div className="ml-auto mr-4 hidden md:flex items-center md:col-start-4">
            {enableShoppingCart && (
              <ShoppingCartNavIcon
                cart={cart}
                openCart={() => setShowCart(true)}
                className="mr-6"
                baseCartAnalyticsArgs={baseCartAnalyticsArgs}
              />
            )}
            {currentUser && (
              <Link
                to={isProvider ? "/provider/account" : "/account/"}
                onClick={analyticsClickHandler({
                  eventName: eventNames.CLICKED_TOP_NAV,
                  eventArgs: { navItem: "Account" },
                })}
              >
                <div className="p-2 rounded-full bg-evvy-blue text-white group">
                  <span className="px-1 uppercase block w-6 h-6 text-center leading-6">
                    {firstLetter}
                  </span>
                </div>
              </Link>
            )}
          </div>

          {/* mobile hamburger */}
          <div className="flex md:hidden items-center col-start-1 row-start-1">
            <button
              ref={burgerRef}
              type="button"
              className="relative flex items-center justify-center w-11 h-11 -ml-2 rounded-md text-evvy-white focus:outline-none focus:ring-evvy-blue"
              aria-controls="mobile-menu"
              aria-expanded={menuOpen}
              onClick={() => setMenuOpen(true)}
            >
              <span className="sr-only">Open main menu</span>
              <span className="relative block w-[22px] h-4">
                <span className="absolute top-0 left-0 w-full h-0.5 bg-evvy-white"></span>
                <span className="absolute top-[7px] left-0 w-full h-0.5 bg-evvy-white"></span>
                <span className="absolute top-[14px] left-0 w-full h-0.5 bg-evvy-white"></span>
              </span>
              {currentUser?.notifications?.care && (
                <span className="absolute top-1.5 right-1 inline-block w-2 h-2 bg-dv-orange rounded-full"></span>
              )}
            </button>
          </div>

          {/* mobile cart */}
          <div className="flex md:hidden items-center justify-end col-start-3 row-start-1">
            {enableShoppingCart && (
              <ShoppingCartNavIcon
                cart={cart}
                openCart={() => setShowCart(true)}
                baseCartAnalyticsArgs={baseCartAnalyticsArgs}
              />
            )}
          </div>

          {menuOpen && (
            <div
              className="fixed inset-0 z-30 md:hidden"
              id="mobile-menu"
              role="dialog"
              aria-modal="true"
            >
              {/* tapping the area the drawer does not cover closes the menu */}
              <div
                className="absolute inset-0 cursor-pointer bg-evvy-black/60 animate-fadeinfast"
                aria-hidden="true"
                onClick={() => setMenuOpen(false)}
              ></div>

              <nav
                ref={drawerRef}
                className="absolute inset-y-0 left-0 flex flex-col w-[270px] max-w-[80%] bg-evvy-black text-evvy-white shadow-lg animate-slide-in-left"
                aria-label="Global"
              >
                <div className="flex items-center justify-between py-4 pl-6 pr-3">
                  <img
                    className="w-auto h-8"
                    src="/images/evvy-logo-text-white.svg"
                    alt="Evvy logo"
                  />
                  <button
                    type="button"
                    className="flex items-center justify-center rounded-md w-11 h-11 focus:outline-none"
                    onClick={() => setMenuOpen(false)}
                  >
                    <span className="sr-only">Close main menu</span>
                    {/* Heroicon name: outline/x */}
                    <svg
                      className="block w-5 h-5"
                      xmlns="http://www.w3.org/2000/svg"
                      fill="none"
                      viewBox="0 0 24 24"
                      stroke="currentColor"
                      aria-hidden="true"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth="2"
                        d="M6 18L18 6M6 6l12 12"
                      />
                    </svg>
                  </button>
                </div>

                <div className="flex-1 px-3 pt-3 overflow-y-auto">
                  {mobileNavTabs.map((tab) => {
                    const isCurrentTab = isCurrentPath(tab.to);
                    return (
                      <Link
                        key={tab.label}
                        to={tab.to}
                        aria-current={isCurrentTab ? "page" : undefined}
                        aria-disabled={!tab.enabled}
                        tabIndex={tab.enabled ? undefined : -1}
                        className={cn(
                          "relative flex items-center h-[52px] px-3 text-base font-medium hover:bg-evvy-white/10",
                          {
                            "pointer-events-none opacity-50": !tab.enabled,
                          }
                        )}
                        onClick={analyticsClickHandler({
                          eventName: eventNames.CLICKED_TOP_NAV,
                          eventArgs: { navItem: tab.label },
                          clickHandler: () => setMenuOpen(false),
                        })}
                      >
                        {isCurrentTab && (
                          <span className="absolute w-[3px] h-7 -left-3 top-1/2 -translate-y-1/2 bg-evvy-blue"></span>
                        )}
                        {tab.label}
                        {tab.showNotificationDot && (
                          <span className="inline-block w-2 h-2 ml-1 align-top rounded-full bg-dv-orange"></span>
                        )}
                      </Link>
                    );
                  })}
                </div>

                {currentUser && (
                  <Link
                    to={accountPath}
                    className="flex items-center px-6 py-4 border-t border-evvy-white/15"
                    onClick={analyticsClickHandler({
                      eventName: eventNames.CLICKED_TOP_NAV,
                      eventArgs: { navItem: "Account" },
                      clickHandler: () => setMenuOpen(false),
                    })}
                  >
                    <div className="flex-shrink-0 p-2 text-white rounded-full bg-evvy-blue">
                      <span className="block w-6 h-6 px-1 leading-6 text-center uppercase">
                        {firstLetter}
                      </span>
                    </div>
                    <div className="flex-1 min-w-0 ml-3">
                      <div className="font-medium truncate">
                        {currentUser.identity.firstName}{" "}
                        {currentUser.identity.lastName}
                      </div>
                      <div className="text-sm truncate text-evvy-white/60">
                        {currentUser.identity.email}
                      </div>
                    </div>
                  </Link>
                )}

                <div className="px-3 pt-1 pb-5">
                  <Link
                    to={accountPath}
                    aria-current={
                      isCurrentPath(accountPath) ? "page" : undefined
                    }
                    className="relative flex items-center h-12 px-3 text-base font-medium hover:bg-evvy-white/10"
                    onClick={analyticsClickHandler({
                      eventName: eventNames.CLICKED_TOP_NAV,
                      eventArgs: { navItem: "Account" },
                      clickHandler: () => setMenuOpen(false),
                    })}
                  >
                    {isCurrentPath(accountPath) && (
                      <span className="absolute w-[3px] h-7 -left-3 top-1/2 -translate-y-1/2 bg-evvy-blue"></span>
                    )}
                    {isProvider ? "Account" : "Account & Subscriptions"}
                  </Link>

                  <button
                    type="button"
                    className="flex items-center w-full h-12 px-3 text-base text-left cursor-pointer hover:bg-evvy-white/10"
                    onClick={() => {
                      setMenuOpen(false);
                      authService.logout();
                    }}
                  >
                    Log out
                  </button>
                </div>
              </nav>
            </div>
          )}
        </div>
        {bottomBanner && (
          <div
            className={`max-w-[1260px] mx-auto transition-opacity duration-500 ${
              isBottomBannerVisible || !isStickyHeader
                ? "opacity-100"
                : "opacity-0"
            }`}
          >
            {bottomBanner}
          </div>
        )}
      </div>
      {/* Initialize the cart component here! */}
      {enableShoppingCart && (
        <PanelCartComponent
          cart={cart}
          isLoading={isLoading}
          addToCart={addToCart}
          removeFromCart={removeFromCart}
          showCart={showCart}
          setShowCart={setShowCart}
          baseCartAnalyticsArgs={baseCartAnalyticsArgs}
          vaginalTest={vaginalTest}
        />
      )}
      {/* Plan Modal - shown when plan link is clicked while disabled */}
      <VHTUpsellModal
        isOpen={showPlanModal}
        onClose={() => setShowPlanModal(false)}
        hasVHTInProgress={hasVHTInProgress}
      />
    </>
  );
};

export default Header;
